import React, { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import api from '../axios/api';
import {loginSuccess } from '../redux/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '../redux/slices/notificationSlice';
import { setSelectedYear } from '../redux/slices/authSlice';


// Login Schema
const LoginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
  captcha: yup.string().required("Captcha verification is required"),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const form = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
      captcha: '',
    },
  });

  const { register, handleSubmit, formState: { errors }, setValue } = form;
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      const response = await api.post("auth/login", data);
      if (response.data.status === '200') {
        const user = response.data?.userDetails;
        const token = response.data?.accessToken;
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(setSelectedYear(user.selectedYear));
        localStorage.setItem("token", token);
        dispatch(loginSuccess({ user: user, token: token }));
        dispatch(
          showNotification({
            message:'Login Successful! Redirecting to your dashboard...',
            type: "success",
          })
        );
        if(response.data.userDetails.roleCode == 'ADMIN'){
          navigate('/masters/users');
        }else if(!response?.data?.userDetails?.company?.id){
          navigate('/account/company-details');
        }else{
          navigate('/account/climate-profile');
        }
       
      } else {
        dispatch(
          showNotification({
            message: response.response.data.message,
            type: "error",
          })
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'An unknown error occurred';
          dispatch(
            showNotification({
              message: error.response.data.message,
              type: "error",
            })
          );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: { xs: '90%', md: '80%', lg: '70%' },
          backgroundColor: 'white',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: 3,
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: 2,
          }}
        >
          <img
            src="/vectors/LoginImage.jpg"
            alt="Login Vector"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Form Section */}
        <Card
          sx={{
            flex: 1,
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CardContent>
            <Typography variant="h4" component="h4" gutterBottom>
              Login to Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enter your credentials to access your dashboard
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      Email
                      <span style={{ color: 'red' }}> *</span>
                    </span>
                  }
                  placeholder="john.doe@example.com"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  {...register('email')}
                />

                <TextField
                  fullWidth
                  label={
                    <span>
                      Password
                      <span style={{ color: 'red' }}> *</span>
                    </span>
                  }
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <EyeOff /> : <Eye />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <a href="/forgetpassword" passHref>
                    <MuiLink>Forgot Password?</MuiLink>
                  </a>
                </Box>

                <Box sx={{ my: 2 }}>
                  <ReCAPTCHA
                    sitekey="6LeybZoqAAAAAGXZMdK6VrvmQHNyWfY-jmEv4lc3"
                    onChange={(token) => setValue('captcha', token || '')}
                  />
                  {errors.captcha && (
                    <Typography color="error" variant="caption">
                      {errors.captcha.message}
                    </Typography>
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                >
                  Login
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2">
                    Don't have an account?{' '}
                    <a href="/signup" passHref>
                      <MuiLink>Sign Up</MuiLink>
                    </a>
                  </Typography>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Login;
